import axios from 'axios';
import { takePreload } from '../lib/preload';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true
});

// R6 — a GET the landing page already started (index.html / lib/preload.js) is answered
// from that request; anything else, or a failed preload, goes to the network as before.
const network = axios.getAdapter(api.defaults.adapter);
api.defaults.adapter = (config) => {
  const hit = config.method === 'get' ? takePreload(api.getUri(config)) : null;
  if (!hit) return network(config);
  return hit.then((r) => (r ? { ...r, config, request: null } : network(config)));
};

// Simple memory cache with TTL
const cache = new Map();
const CACHE_TTL = 60000; // 1 minute cache

const getCached = (key) => {
  const item = cache.get(key);
  if (item && Date.now() < item.expiry) {
    return item.data;
  }
  cache.delete(key);
  return null;
};

const setCache = (key, data, ttl = CACHE_TTL) => {
  cache.set(key, { data, expiry: Date.now() + ttl });
};

// Cached GET request
const cachedGet = async (url, params = {}, ttl = CACHE_TTL) => {
  const cacheKey = `${url}:${JSON.stringify(params)}`;
  const cached = getCached(cacheKey);
  if (cached) return cached;
  
  const response = await api.get(url, { params });
  setCache(cacheKey, response, ttl);
  return response;
};

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Artists API with caching for frequently accessed data
export const artistsApi = {
  getAll: (params) => api.get('/artists', { params }),
  getFeatured: (city) => cachedGet('/artists/featured', { city }, 120000), // 2 min cache
  getById: (id) => cachedGet(`/artists/${id}`, {}, 60000), // 1 min cache
  resolve: (params) => api.get('/artists/resolve', { params }),
  getCategories: () => cachedGet('/artists/categories', {}, 300000), // 5 min cache
  getCities: () => cachedGet('/artists/cities', {}, 300000), // 5 min cache
  create: (data) => api.post('/artists', data),
  aiSearch: (data) => api.post('/search/ai', data),
  getReviews: (artistId, params) => api.get(`/reviews/artist/${artistId}`, { params }),
  getDynamicPrice: (artistId, eventDate) => api.get(`/artists/${artistId}/price`, { params: { event_date: eventDate } }),
  getRecommendations: (artistId, limit = 6) => cachedGet(`/artists/${artistId}/recommendations`, { limit }, 60000),
  getHomepageRecommendations: (city, limit = 8) => cachedGet('/recommendations/homepage', { city, limit }, 120000),
  // New enhanced profile endpoints
  trackLead: (artistId, source = 'profile_view') => api.post(`/artists/${artistId}/track-lead`, null, { params: { source } }),
  getStats: (artistId) => api.get(`/artists/${artistId}/stats`),
  getAvailability: (artistId, month) => api.get(`/artists/${artistId}/availability`, { params: { month } }),
  updateAvailability: (artistId, dates) => api.put(`/artists/${artistId}/availability`, dates),
  getYoutubeThumbnail: (artistId, videoUrl) => api.get(`/artists/${artistId}/youtube-thumbnail`, { params: { video_url: videoUrl } }),
  // Phase 1-6 Form Options
  getFormOptions: () => cachedGet('/artists/form-options', {}, 600000), // 10 min cache
  getTechRiderTemplate: (artistType) => api.get(`/artists/tech-rider-template/${encodeURIComponent(artistType)}`),
  validateSocialLinks: (instagram, youtube) => api.post('/artists/validate-social-links', null, { params: { instagram_url: instagram, youtube_url: youtube } }),
  validateVideoLinks: (videoLinks) => api.post('/artists/validate-video-links', videoLinks),
  updateTechRider: (data) => api.put('/artists/profile/tech-rider', data),
  lockTechRider: (artistId) => api.post(`/artists/profile/lock-tech-rider/${artistId}`),
  unlockTechRider: (artistId) => api.post(`/artists/profile/unlock-tech-rider/${artistId}`),
  // Cache invalidation helper
  clearCache: () => cache.clear()
};

// Bookings API
export const bookingsApi = {
  create: (data) => api.post('/bookings', data),
  getAll: () => api.get('/bookings'),
  getById: (bookingId) => api.get(`/bookings/${bookingId}`),
  updateStatus: (id, status) => api.put(`/bookings/${id}/status`, null, { params: { status } }),
  
  // Pricing - Pass all parameters for accurate calculation
  getPricing: (artistId, eventDate, options = {}) => api.get(`/bookings/pricing/${artistId}`, { 
    params: { 
      event_date: eventDate,
      duration_hours: options.duration_hours,
      num_people: options.num_people,
      tattoo_size: options.tattoo_size
    } 
  }),
  
  // Counter Offer Response
  respondToCounter: (bookingId, action, message) => api.post(`/bookings/${bookingId}/respond-to-counter`, { action, message }),
  
  // Cancellation
  getCancellationPreview: (bookingId) => api.get(`/bookings/${bookingId}/cancellation-preview`),
  cancel: (bookingId, reason, remarks) => api.post(`/bookings/${bookingId}/cancel`, { reason, remarks }),
  getAlternateArtists: (bookingId) => api.get(`/bookings/${bookingId}/alternate-artists`),
  checkShortNotice: (eventDate) => api.get('/bookings/check-short-notice', { params: { event_date: eventDate } }),
  
  // Refund Tracking
  getRefundStatus: (bookingId) => api.get(`/bookings/${bookingId}/refund-status`),
  getRefundStatuses: () => api.get('/bookings/refund-statuses')
};

// Artist Dashboard API
export const artistApi = {
  getProfile: () => api.get('/artist/profile'),
  updateProfile: (data) => api.put('/artist/profile', data),
  saveDraft: (data) => api.post('/artist/profile/save-draft', data),
  submitForApproval: (data) => api.post('/artist/profile/submit-for-approval', data),
  getProfileStatus: () => api.get('/artist/profile/status'),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/artists/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getBookings: (status) => api.get('/artist/bookings', { params: { status } }),
  respondToBooking: (bookingId, action, _unused, message) => 
    api.post(`/artist/bookings/${bookingId}/respond`, { 
      action, 
      message 
    }),
  getStats: () => api.get('/artist/stats'),
  
  // Availability Management
  getAvailability: (startDate, endDate) => api.get('/artist/availability', { 
    params: { start_date: startDate, end_date: endDate } 
  }),
  blockDates: (dates) => api.post('/artist/availability/block', { dates }),
  unblockDates: (dates) => api.post('/artist/availability/unblock', { dates }),
  
  // Artist Cancellation
  canCancelBooking: (bookingId) => api.get(`/artist/bookings/${bookingId}/can-cancel`),
  cancelBooking: (bookingId, reason, remarks) => api.post(`/artist/bookings/${bookingId}/cancel`, { reason, remarks }),
  
  // OTP-based event completion
  generateCompletionOtp: (bookingId) => api.post(`/artist/bookings/${bookingId}/generate-completion-otp`),
  verifyCompletionOtp: (bookingId, otp) => api.post(`/artist/bookings/${bookingId}/verify-completion-otp`, { otp })
};

// P22 — Artist dashboard / reveals / ranking / share (artist-only).
export const artistPortalApi = {
  dashboard: () => api.get('/artist/me/dashboard'),
  ranking: () => api.get('/artist/me/ranking'),
  reveals: () => api.get('/artist/me/reveals'),
  markBooked: (revealId) => api.post(`/artist/me/reveals/${revealId}/booked`),
  remindReviews: () => api.post('/artist/me/review-reminders'),
  share: () => api.get('/artist/me/share'),
};

// Quote Requests API
export const quotesApi = {
  create: (data) => api.post('/quotes/request', data),
  getAll: () => api.get('/quotes/requests'),
  getById: (id) => api.get(`/quotes/${id}`),
  respond: (quoteId, data) => api.post(`/quotes/${quoteId}/respond`, data),
  accept: (quoteId, artistId) => api.post(`/quotes/${quoteId}/accept/${artistId}`)
};

// Reviews API
export const reviewsApi = {
  create: (data) => api.post('/reviews', data),
  getForArtist: (artistId, params) => api.get(`/reviews/artist/${artistId}`, { params }),
  createCustomerReview: (data) => api.post('/reviews/customer', data),
  getPendingReviews: () => api.get('/reviews/my-pending')
};

// P20 — performance check + review token (public; token is the auth)
export const performanceChecksApi = {
  getToken: (token) => api.get(`/performance-checks/token/${token}`),
  markViewed: (token) => api.post(`/performance-checks/token/${token}/viewed`),
  submit: (token, data) => api.post(`/performance-checks/token/${token}`, data),
  recordNo: (token) => api.post(`/performance-checks/token/${token}/no`),
};

// Invoice API (Customer)
export const invoiceApi = {
  getMyGSTDetails: () => api.get('/invoices/customer/gst-details'),
  saveMyGSTDetails: (details) => api.post('/invoices/customer/gst-details', details),
  validateGST: (gstNumber) => api.post(`/invoices/validate-gst?gst_number=${gstNumber}`),
  getInvoiceByBooking: (bookingId) => api.get(`/invoices/booking/${bookingId}`),
  downloadInvoicePDF: (invoiceId) => api.get(`/invoices/${invoiceId}/pdf`)
};

// Revenue Analytics API (Admin only)
export const revenueApi = {
  getSummary: (period, customStart, customEnd) => {
    const params = { period };
    if (customStart) params.custom_start = customStart;
    if (customEnd) params.custom_end = customEnd;
    return api.get('/revenue/summary', { params });
  },
  getBreakdown: (params) => api.get('/revenue/breakdown', { params }),
  getChartData: (period, customStart, customEnd) => {
    const params = { period };
    if (customStart) params.custom_start = customStart;
    if (customEnd) params.custom_end = customEnd;
    return api.get('/revenue/chart-data', { params });
  },
  exportData: (format, period, customStart, customEnd) => {
    const params = { format, period };
    if (customStart) params.custom_start = customStart;
    if (customEnd) params.custom_end = customEnd;
    return api.get('/revenue/export', { params });
  }
};

// Notifications API
export const notificationsApi = {
  getAll: (unreadOnly = false) => api.get('/notifications', { params: { unread_only: unreadOnly } }),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all')
};

// Admin API
export const adminApi = {
  // Stats
  getStats: () => api.get('/admin/stats'),
  
  // Reapprovals - Approved artists with pending profile changes
  getReapprovals: (page = 1, limit = 50) => api.get('/admin/reapprovals', { params: { page, limit } }),
  getReapprovalCount: () => api.get('/admin/reapprovals/count'),

  // P19 — Approvals queue (new signups + staged changes)
  getApprovals: () => api.get('/admin/approvals'),
  getApprovalsCount: () => api.get('/admin/approvals/count'),
  approveItem: (artistId, type) => api.post(`/admin/approvals/${artistId}/approve`, { type }),
  requestApprovalChanges: (artistId, type, note) => api.post(`/admin/approvals/${artistId}/request-changes`, { type, note }),
  rejectItem: (artistId, type, reason) => api.post(`/admin/approvals/${artistId}/reject`, { type, reason }),
  setCoverFlag: (artistId, flagged) => api.post(`/admin/approvals/${artistId}/cover-flag`, { flagged }),
  
  // Changes Requested - Artists asked to make profile changes
  getChangesRequestedArtists: (page = 1, limit = 50) => api.get('/admin/changes-requested', { params: { page, limit } }),
  
  // User Management
  getUsers: (role, search, page = 1, limit = 50) => {
    const params = { page, limit };
    if (role) params.role = role;
    if (search) params.search = search;
    return api.get('/admin/users', { params });
  },
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, null, { params: data }),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  
  // Artist Management
  getArtists: (status, page = 1, search = '', limit = 50) => api.get('/admin/artists', { params: { status, page, limit, search: search || undefined } }),
  getArtistDetail: (artistId) => api.get(`/admin/artists/${artistId}`),
  updateArtist: (artistId, data) => api.put(`/admin/artists/${artistId}`, data),
  updateArtistStatus: (id, status, rejectionReason = null, changesRequested = null) => 
    api.put(`/admin/artists/${id}/status`, null, { 
      params: { 
        status, 
        rejection_reason: rejectionReason,
        changes_requested: changesRequested
      } 
    }),
  bulkUpdateArtistStatus: (artistIds, status, reason = null) => 
    api.post('/admin/artists/bulk-status', { 
      artist_ids: artistIds, 
      status, 
      reason 
    }),
  
  // Booking Management
  getBookings: (status) => api.get('/admin/bookings', { params: { status } }),
  getDetailedBookings: (status, page = 1, limit = 50, search = null) => api.get('/admin/bookings/detailed', { params: { status, page, limit, search } }),
  getBookingDetail: (bookingId) => api.get(`/admin/bookings/${bookingId}`),
  updateBookingStatus: (bookingId, status, adminNote) => api.put(`/admin/bookings/${bookingId}/status`, null, { params: { status, admin_note: adminNote } }),
  markPaymentReceived: (bookingId, amount, method, reference) => api.put(`/admin/bookings/${bookingId}/payment`, null, { params: { amount, payment_method: method, payment_reference: reference } }),
  confirmPayment: (bookingId, paymentData) => api.post(`/bookings/${bookingId}/confirm-payment`, paymentData),
  markBookingCompleted: (bookingId) => api.put(`/admin/bookings/${bookingId}/complete`),
  
  // Admin Cancellation
  getCancellationReasons: () => api.get('/admin/cancellation-reasons'),
  cancelBooking: (bookingId, reason, remarks) => api.post(`/admin/bookings/${bookingId}/cancel`, { reason, remarks }),
  getAlternateArtists: (bookingId) => api.get(`/admin/bookings/${bookingId}/alternate-artists`),
  getCancellationAuditLogs: (bookingId, page, limit) => api.get('/admin/cancellation-audit-logs', { params: { booking_id: bookingId, page, limit } }),
  
  // Refund Management
  getRefundStatuses: () => api.get('/admin/refund-statuses'),
  getRefundStatistics: () => api.get('/admin/refund-statistics'),
  getBookingRefundStatus: (bookingId) => api.get(`/admin/bookings/${bookingId}/refund-status`),
  updateRefundStatus: (bookingId, status, refundReference, failureReason, notes) => 
    api.put(`/admin/bookings/${bookingId}/refund-status`, { 
      status, 
      refund_reference: refundReference, 
      failure_reason: failureReason, 
      notes 
    }),
  getPendingRefunds: (page, limit) => api.get('/admin/refunds/pending', { params: { page, limit } }),
  
  // Reviews management
  getAllReviews: (params = {}) => api.get('/admin/reviews', { params }),
  createReview: (data) => api.post('/admin/reviews', data),
  updateReview: (reviewId, data) => api.put(`/admin/reviews/${reviewId}`, null, { params: data }),
  moderateReview: (reviewId, action) => api.post(`/admin/reviews/${reviewId}/moderate`, { action }),
  deleteReview: (reviewId) => api.delete(`/admin/reviews/${reviewId}`),
  
  // Bulk upload
  bulkUploadArtists: (formData, status = 'draft') => api.post(`/management/artists/bulk-upload?status=${status}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getBulkUploadLogs: () => api.get('/management/bulk-uploads'),
  
  // Festival Calendar Management
  getFestivals: (year, includeInactive = true) => api.get('/festivals/admin/all', { params: { year, include_inactive: includeInactive } }),
  createFestival: (data) => api.post('/festivals/admin/create', data),
  updateFestival: (festivalId, data) => api.put(`/festivals/admin/${festivalId}`, data),
  deleteFestival: (festivalId, hardDelete = false) => api.delete(`/festivals/admin/${festivalId}`, { params: { hard_delete: hardDelete } }),
  bulkCreateFestivals: (festivals) => api.post('/festivals/admin/bulk-create', festivals),
  
  // Scheduler Management
  getSchedulerStatus: () => api.get('/admin/scheduler/status'),
  runSchedulerJob: (jobId) => api.post(`/admin/scheduler/run/${jobId}`),
  checkExpiredPayments: () => api.post('/admin/bookings/check-expired-payments'),
  
  // WhatsApp Management
  getWhatsAppStatus: () => api.get('/admin/whatsapp/status'),
  sendTestWhatsApp: (phone, message) => api.post('/admin/whatsapp/test', { phone, message }),
  
  // Email Management
  getEmailStatus: () => api.get('/admin/email/status'),
  sendTestEmail: (email, subject, message) => api.post('/admin/email/test', { email, subject, message }),
  sendCustomEmail: (recipients, subject, message) => api.post('/admin/email/send', { recipients, subject, message }),
  
  // SMS Management
  getSMSStatus: () => api.get('/sms/status'),
  
  // Invoice Management
  getInvoices: (page = 1, limit = 50, dateFrom, dateTo, invoiceType) => 
    api.get('/invoices', { params: { page, limit, date_from: dateFrom, date_to: dateTo, invoice_type: invoiceType } }),
  getInvoice: (invoiceId) => api.get(`/invoices/${invoiceId}`),
  getInvoiceByBooking: (bookingId) => api.get(`/invoices/booking/${bookingId}`),
  updateInvoiceMetadata: (invoiceId, metadata) => api.put(`/invoices/${invoiceId}/metadata`, metadata),
  deleteInvoice: (invoiceId) => api.delete(`/invoices/${invoiceId}`),
  downloadInvoicePDF: (invoiceId) => api.get(`/invoices/${invoiceId}/pdf`),
  regenerateInvoicePDF: (invoiceId) => api.post(`/invoices/${invoiceId}/regenerate-pdf`),
  resendInvoiceEmail: (invoiceId) => api.post(`/invoices/${invoiceId}/send-email`),
  bulkDownloadInvoices: (dateFrom, dateTo) => 
    api.post(`/invoices/admin/bulk-download?date_from=${dateFrom}&date_to=${dateTo}`),
  
  // Company Config (Accounts)
  getCompanyConfig: () => api.get('/invoices/admin/company-config'),
  updateCompanyConfig: (config) => api.put('/invoices/admin/company-config', config),
  getSMSTemplates: () => api.get('/sms/admin/templates'),
  getSMSTemplate: (templateName) => api.get(`/sms/admin/templates/${templateName}`),
  createSMSTemplate: (data) => api.post('/sms/admin/templates', data),
  updateSMSTemplate: (templateName, data) => api.put(`/sms/admin/templates/${templateName}`, data),
  deleteSMSTemplate: (templateName) => api.delete(`/sms/admin/templates/${templateName}`),
  seedSMSTemplates: () => api.post('/sms/admin/templates/seed'),
  sendTestSMS: (phone, templateName, variables) => api.post('/sms/admin/test', { phone, template_name: templateName, variables }),
  getSMSLogs: (params) => api.get('/sms/admin/logs', { params }),
  getSMSStats: () => api.get('/sms/admin/stats'),
  
  // Admin Flags Management
  getArtistFlags: (artistId) => api.get(`/admin/artists/${artistId}/flags`),
  updateArtistFlags: (artistId, flags) => api.put(`/admin/artists/${artistId}/flags`, null, { params: flags }),
  getFeaturedArtists: () => api.get('/admin/artists/featured'),
  
  // Profile Deletion
  checkArtistCanDelete: (artistId) => api.get(`/admin/artists/${artistId}/can-delete`),
  deleteArtistProfile: (artistId) => api.delete(`/admin/artists/${artistId}/profile`),
  checkCustomerCanDelete: (customerId) => api.get(`/admin/customers/${customerId}/can-delete`),
  deleteCustomerProfile: (customerId) => api.delete(`/admin/customers/${customerId}/profile`),
  getDeletionLogs: (entityType, page = 1) => api.get('/admin/deletion-logs', { params: { entity_type: entityType, page } }),
  
  // Quick Responder Management
  getArtistResponseStats: (artistId) => api.get(`/admin/artists/${artistId}/response-stats`),
  recalculateQuickResponder: (artistId) => api.post(`/admin/artists/${artistId}/recalculate-quick-responder`),
  recalculateAllQuickResponders: () => api.post('/admin/quick-responder/recalculate-all'),
  
  // Admin Messages/Conversations
  adminGetConversations: (limit = 20, offset = 0) => api.get('/admin/messages/conversations', { params: { limit, offset } }),
  adminGetConversation: (conversationId) => api.get(`/admin/messages/conversations/${conversationId}`),
  
  // Data Export - Artists and Customers
  exportArtistsKYC: (status = null) => api.get('/admin/export/artists-kyc', { params: { status } }),
  exportCustomers: () => api.get('/admin/export/customers'),
  
  // RBAC - Role-Based Access Control
  getMyPermissions: () => api.get('/admin/rbac/my-permissions'),
  getRolesInfo: () => api.get('/admin/rbac/roles-info'),
  
  // RBAC - Management Users
  getManagementUsers: (role = null, includeInactive = false) => 
    api.get('/admin/rbac/management-users', { params: { role, include_inactive: includeInactive } }),
  createManagementUser: (data) => api.post('/admin/rbac/management-users', data),
  toggleManagementUserStatus: (userId, isActive) => 
    api.put(`/admin/rbac/management-users/${userId}/status`, null, { params: { is_active: isActive } }),
  
  // RBAC - Designer Actions
  designerEditMedia: (artistId, data) => api.post(`/admin/rbac/designer/artists/${artistId}/media`, data),
  getPendingDesignerChanges: (artistId = null, page = 1, limit = 50) => 
    api.get('/admin/rbac/designer/pending-changes', { params: { artist_id: artistId, page, limit } }),
  approveDesignerChange: (changeId) => api.post(`/admin/rbac/admin/pending-changes/${changeId}/approve`),
  rejectDesignerChange: (changeId, rejectionReason = null) => 
    api.post(`/admin/rbac/admin/pending-changes/${changeId}/reject`, { rejection_reason: rejectionReason }),
  
  // RBAC - Artist Manager Actions
  managerForwardToAdmin: (artistId, notes = null) => 
    api.post(`/admin/rbac/manager/artists/${artistId}/forward`, { notes }),
  managerRequestChanges: (artistId, changesRequested) => 
    api.post(`/admin/rbac/manager/artists/${artistId}/request-changes`, { changes_requested: changesRequested }),
  
  // RBAC - Forwarded Profiles (Admin only)
  getForwardedProfiles: (page = 1, limit = 50) => 
    api.get('/admin/rbac/forwarded-profiles', { params: { page, limit } }),
  getForwardedProfilesCount: () => 
    api.get('/admin/rbac/forwarded-profiles/count'),
  
  // RBAC - Audit Logs
  getAuditLogs: (role = null, userId = null, action = null, page = 1, limit = 50) => 
    api.get('/admin/rbac/audit-logs', { params: { role, user_id: userId, action, page, limit } }),
  getDesignerActivityLogs: (designerId = null, page = 1, limit = 50) => 
    api.get('/admin/rbac/audit-logs/designer', { params: { designer_id: designerId, page, limit } }),
  getArtistManagerActivityLogs: (managerId = null, page = 1, limit = 50) => 
    api.get('/admin/rbac/audit-logs/artist-manager', { params: { manager_id: managerId, page, limit } }),
  
  // Taxonomy Management
  getNeedsTaxonomyReview: (page = 1, limit = 50) => 
    api.get('/admin/artists/needs-taxonomy-review', { params: { page, limit } }),
  updateArtistTaxonomy: (artistId, data) => 
    api.put(`/admin/artists/${artistId}/taxonomy`, data),
  bulkUpdateTaxonomy: (artistIds, primarySlug) => 
    api.post('/admin/artists/bulk-taxonomy', { 
      artist_ids: artistIds, 
      primary_slug: primarySlug, 
      taxonomy_slugs: [primarySlug] 
    })
};

// Festival API (public)
export const festivalsApi = {
  getUpcoming: (year, limit = 20) => api.get('/festivals/upcoming', { params: { year, limit } }),
  checkDate: (date) => api.get('/festivals/check-date', { params: { check_date: date } }),
  getByYear: (year) => api.get(`/festivals/year/${year}`)
};

// Verification API
export const verificationApi = {
  getStatus: () => api.get('/verification/status'),
  sendOTP: (channel, phone, email) => api.post('/verification/send-otp', { channel, phone, email }),
  verifyOTP: (channel, phone, email, code) => api.post('/verification/verify-otp', { channel, phone, email, code })
};

// Messaging API
export const messagesApi = {
  // Conversations
  getConversations: (limit = 20, offset = 0) => api.get('/messages/conversations', { params: { limit, offset } }),
  getConversation: (conversationId) => api.get(`/messages/conversations/${conversationId}`),
  startConversation: (artistId, initialMessage) => api.post('/messages/conversations', { artist_id: artistId, initial_message: initialMessage }),
  artistInitiateConversation: (customerId) => api.post('/messages/conversations/artist-initiate', { customer_id: customerId }),
  
  // Booking-specific conversations
  getBookingConversation: (bookingId) => api.get(`/messages/booking/${bookingId}`),
  
  // Messages
  sendMessage: (conversationId, content) => api.post(`/messages/conversations/${conversationId}/messages`, { content }),
  markAsRead: (conversationId) => api.put(`/messages/conversations/${conversationId}/read`),
  
  // Unread count
  getUnreadCount: () => api.get('/messages/unread-count'),
  
  // Admin
  adminGetConversations: (limit = 20, offset = 0) => api.get('/admin/messages/conversations', { params: { limit, offset } }),
  adminGetConversation: (conversationId) => api.get(`/admin/messages/conversations/${conversationId}`)
};

// WebSocket URL for messaging
export const getWebSocketUrl = (userId) => {
  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const apiUrl = process.env.REACT_APP_BACKEND_URL?.replace(/^https?:\/\//, '');
  if (!apiUrl) {
    console.error('REACT_APP_BACKEND_URL is not configured');
    return null;
  }
  return `${wsProtocol}//${apiUrl}/api/messages/ws/${userId}`;
};

// Taxonomy API
export const taxonomyApi = {
  // Get full taxonomy tree
  getTaxonomy: () => cachedGet('/taxonomy', {}, 300000), // 5 min cache
  
  // Get tags (occasions and festivals)
  getTags: () => cachedGet('/taxonomy/tags', {}, 300000), // 5 min cache
  
  // Get category by slug
  getCategoryBySlug: async (slug) => {
    try {
      const response = await api.get(`/taxonomy/category/${slug}`);
      return response.data;
    } catch (error) {
      return null;
    }
  },
  
  // Get all sections
  getSections: () => cachedGet('/taxonomy/sections', {}, 300000),
  
  // Search categories
  searchCategories: (query, limit = 20) => api.get('/taxonomy/search', { params: { q: query, limit } }),
  
  // Get flat list of all categories
  getFlatCategories: () => cachedGet('/taxonomy/flat', {}, 300000)
};

// P14 — Contact reveal + reveal log
export const revealsApi = {
  reveal: (data) => api.post('/reveals', data),
  adminList: (params) => api.get('/admin/reveals', { params }),
  exportCsv: (params) => api.get('/admin/reveals/export.csv', { params, responseType: 'blob' }),
  adminCustomer: (id) => api.get(`/admin/customers/${id}`),
};

// P15 — Collections (public + admin)
export const collectionsApi = {
  listPublic: () => api.get('/collections'),
  getPublic: (slug) => api.get(`/collections/${slug}`),
  adminList: () => api.get('/admin/collections'),
  adminCreate: (data) => api.post('/admin/collections', data),
  adminSeed: () => api.post('/admin/collections/seed'),
  adminReorder: (orderedIds) => api.put('/admin/collections/reorder', { ordered_ids: orderedIds }),
  adminUpdate: (id, data) => api.put(`/admin/collections/${id}`, data),
};

// P15 — Saved (the shortlist). Saving never requires login.
export const savedApi = {
  get: () => api.get('/saved'),
  resolve: (ids) => api.post('/saved/resolve', { artist_ids: ids }),
  add: (id) => api.post(`/saved/${id}`),
  remove: (id) => api.delete(`/saved/${id}`),
  merge: (ids) => api.post('/saved/merge', { artist_ids: ids }),
};

// P15 — Post a requirement (capped at 5 artists server-side)
export const postRequirementApi = {
  post: (data) => api.post('/post-requirement', data),
  mine: () => api.get('/post-requirement/mine'),
};

// R3 — artist request inbox + admin requirements table
export const artistRequestsApi = {
  list: () => api.get('/artist/requests'),
  get: (id) => api.get(`/artist/requests/${id}`),
  reply: (id) => api.post(`/artist/requests/${id}/reply`),
  decline: (id) => api.post(`/artist/requests/${id}/decline`),
};

export const adminRequirementsApi = {
  list: (params) => api.get('/admin/requirements', { params }),
  exportCsv: (params) => api.get('/admin/requirements/export.csv', { params, responseType: 'blob' }),
  sendMore: (id) => api.post(`/admin/requirements/${id}/send-more`),
};

// P16 — Artist signup wizard (phone-OTP account + draft save/resume + publish)
export const artistSignupApi = {
  otpSend: (phone) => api.post('/artist/signup/otp/send', { phone }),
  otpVerify: (phone, code, attribution) => api.post('/artist/signup/otp/verify', { phone, code, attribution }),
  getDraft: () => api.get('/artist/signup/draft'),
  saveDraft: (step, data) => api.put('/artist/signup/draft', { step, data }),
  validateVideo: (url) => api.post('/artist/signup/validate-video', { url }),
  publish: () => api.post('/artist/signup/publish'),
};

// F4 — private staff (team) phone-OTP sign-in
export const teamAuthApi = {
  otpSend: (phone) => api.post('/team/otp/send', { phone }),
  otpVerify: (phone, code) => api.post('/team/otp/verify', { phone, code }),
};

// F4 — admin-only team management
export const adminTeamApi = {
  list: () => api.get('/admin/team'),
  add: (data) => api.post('/admin/team', data),
  toggle: (teamId) => api.put(`/admin/team/${teamId}/toggle`),
  remove: (teamId) => api.delete(`/admin/team/${teamId}`),
  audit: () => api.get('/admin/team/audit'),
};

// F4 — artist phone-OTP sign-in (distinct from signup)
export const artistLoginApi = {
  otpSend: (phone) => api.post('/artist/login/otp/send', { phone }),
  otpVerify: (phone, code) => api.post('/artist/login/otp/verify', { phone, code }),
};

// P18 — admin profile-improvement nudges
export const nudgesApi = {
  list: () => api.get('/admin/nudges'),
  build: () => api.post('/admin/nudges/build'),
  toggle: (enabled) => api.post('/admin/nudges/toggle', { enabled }),
  testSend: () => api.post('/admin/nudges/test-send'),
};

// P25 — SEO verification tags (public read, admin write)
export const seoApi = {
  getVerification: () => api.get('/seo/verification'),
  setVerification: (body) => api.post('/admin/seo/verification', body),
  getSite: () => api.get('/site-settings'),
  setSite: (body) => api.put('/admin/site-settings', body),
};

// P23 — admin WhatsApp message log + marketing pause switch
export const waMessagesApi = {
  list: (limit = 200) => api.get('/admin/wa-messages', { params: { limit } }),
  dailyCounts: () => api.get('/admin/wa-messages/daily-counts'),
  getPause: () => api.get('/admin/wa-messages/pause'),
  setPause: (paused) => api.post('/admin/wa-messages/pause', { paused }),
  resend: (messageId) => api.post(`/admin/wa-messages/${messageId}/resend`),
};

// P17 — Profile editor with completeness
export const artistEditorApi = {
  get: () => api.get('/artist/editor'),
  saveSection: (section, data) => api.put(`/artist/editor/section/${section}`, { data }).then((r) => r.data),
};

// P26 — Price guides + Explore accordion
export const priceGuideApi = {
  home: () => api.get('/price-guide/home'),
  get: (slug) => api.get(`/price-guide/${slug}`),
  adminGet: (slug) => api.get(`/price-guide/admin/${slug}`),
  adminSave: (slug, body) => api.put(`/price-guide/admin/${slug}`, body),
};

export const exploreApi = {
  get: () => api.get('/explore'),
};

// P27 — Web Vitals RUM + admin insight screens
export const webVitalsApi = {
  report: (body) => api.post('/web-vitals', body),
};

export const insightsApi = {
  searchTerms: (days = 30) => api.get('/admin/search-terms', { params: { days } }),
  demandGaps: () => api.get('/admin/demand-gaps'),
  webVitals: (days = 7) => api.get('/admin/web-vitals', { params: { days } }),
};

export default api;
